import React, { useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminAPI } from '../services/api';
import StreamingMessage from '../components/StreamingMessage';
import InteractiveCards from '../components/InteractiveCards';
import CardErrorBoundary from '../components/CardErrorBoundary';

const formatDateTime = (iso) => {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return String(iso);
  }
};

const AdminChatDetailPage = () => {
  const { chatId } = useParams();

  const { data: chatResponse, isLoading: chatLoading, isError: chatError } = useQuery({
    queryKey: ['admin-chat', chatId],
    queryFn: async () => {
      const response = await adminAPI.getChat(chatId);
      return response.data;
    },
    enabled: !!chatId,
  });

  const {
    data: messagesResponse,
    isLoading: messagesLoading,
  } = useQuery({
    queryKey: ['admin-chat-messages', chatId],
    queryFn: async () => {
      const response = await adminAPI.getChatMessages(chatId);
      return response.data;
    },
    enabled: !!chatId,
  });

  const chat = chatResponse?.chat;
  const project = chat?.project || {};
  const user = chat?.user || {};
  // Messages are already in chronological order (oldest first) from backend
  const messages = useMemo(() => messagesResponse?.messages || [], [messagesResponse]);
  const totalMessages = messagesResponse?.total ?? chat?.messageCount ?? 0;

  // Resolve source image URL for a given assistant message
  // Looks at message_metadata.source_image_url or finds the preceding user message with an image
  const getSourceImageForMessage = useCallback((assistantMessageId) => {
    if (!assistantMessageId || messages.length === 0) return null;
    
    const assistantMsg = messages.find(m => m.id === assistantMessageId);
    const aMeta = assistantMsg?.message_metadata || {};
    
    // Check if the assistant message has source_image_url directly
    if (aMeta.source_image_url) {
      return aMeta.source_image_url;
    }
    
    // Check if there's a source_user_message_id reference
    if (aMeta.source_user_message_id) {
      const userMsg = messages.find(m => m.id === aMeta.source_user_message_id && m.role === 'user');
      const url = userMsg?.message_metadata?.imageUrl;
      if (url) return url;
    }
    
    // Fall back to finding the most recent preceding user message with an image
    const idx = messages.findIndex(m => m.id === assistantMessageId);
    if (idx > 0) {
      for (let i = idx - 1; i >= 0; i--) {
        const candidate = messages[i];
        if (candidate?.role === 'user') {
          const url = candidate?.message_metadata?.imageUrl;
          if (url) return url;
        }
      }
    }
    
    return null;
  }, [messages]);

  if (chatLoading || messagesLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Session Transcript
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Loading session details...
          </p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500 dark:text-gray-400 text-sm">Loading...</div>
        </div>
      </div>
    );
  }

  if (chatError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Session Transcript
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Unable to load session details.
          </p>
        </div>
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Failed to load session.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-gray-900 dark:text-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Session Transcript
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Full chat log between the user and Okio agent.
          </p>
        </div>
        <Link
          to="/admin/chats"
          className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
        >
          Back to sessions
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Session
          </div>
          <div className="mt-2 text-sm text-gray-700 dark:text-gray-200">
            <div className="font-medium text-gray-900 dark:text-gray-100">{chat?.title || 'Untitled session'}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{chat?.id}</div>
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Created: {formatDateTime(chat?.createdAt)}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Updated: {formatDateTime(chat?.updatedAt)}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Messages: {totalMessages}
            </div>
          </div>
        </div>
        <div className="rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Project
          </div>
          <div className="mt-2 text-sm text-gray-700 dark:text-gray-200">
            <div className="font-medium text-gray-900 dark:text-gray-100">{project.name || '–'}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{project.id || '–'}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Status: {project.status || '–'}
            </div>
          </div>
        </div>
        <div className="rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            User
          </div>
          <div className="mt-2 text-sm text-gray-700 dark:text-gray-200">
            <div className="font-medium text-gray-900 dark:text-gray-100">{user.username || '–'}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{user.email || '–'}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">{user.id || '–'}</div>
          </div>
        </div>
      </div>

      <div className="rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
            Messages ({totalMessages})
          </div>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-4 pb-6 chat-container">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500 dark:text-gray-400 text-sm">No messages found.</div>
            </div>
          ) : (
            messages.map((message) => {
              const cards = message.message_metadata?.interactive_cards || [];
              const handledCardIds = message.message_metadata?.handled_cards || [];
              const cardStates = message.message_metadata?.card_states || {};
              const sourceImageUrl = message.role === 'assistant' ? getSourceImageForMessage(message.id) : null;
              
              return (
                <div
                  key={message.id}
                  className={message.role === 'user' ? 'card-base-user' : 'card-base-agent'}
                >
                  <StreamingMessage message={message} />
                  {message.role === 'assistant' && cards.length > 0 && (
                    <CardErrorBoundary>
                      <InteractiveCards
                        cards={cards}
                        messageId={message.id}
                        isReadOnly={true}
                        handledCardIds={handledCardIds}
                        cardStates={cardStates}
                        sourceImageUrl={sourceImageUrl}
                        assistantMessageId={message.id}
                        allMessages={messages}
                        croppedImages={{}}
                      />
                    </CardErrorBoundary>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminChatDetailPage;
